import { apiFetch } from "@/lib/api";
import type { WorkOrderDetail, WorkOrderListResponse } from "./workOrdersPageModel";

/** Finished goods currently run on the assembly page (packaged reset magnets). */
export const ASSEMBLY_FINISHED_GOOD_SKUS = ["FI-3C MG", "FI-5A MG", "FI-5A-W MG"] as const;

export type AssemblyWorkOrderType = "label" | "serialized" | "build_order";

export interface AssemblyKind {
  id: string;
  label: string;
  description: string;
  outputSku: string;
  sourceSkus: string[];
  componentSwapSlots?: AssemblyComponentSwapSlot[];
}

export interface AssemblyComponentSwapSlot {
  id: string;
  label: string;
  variable_names: string[];
}

export interface AssemblyWorkOrderListRow {
  assembly_type: AssemblyWorkOrderType;
  assembly_kind: string | null;
  certified_count: number | null;
}

export interface AssemblyWorkOrderDetail extends WorkOrderDetail {
  assembly_type: AssemblyWorkOrderType;
  assembly_kind: string | null;
  certified_count: number | null;
  source_skus: string[] | null;
  kind_label: string | null;
  kind_description: string | null;
  instruction_revision: number | null;
  instruction_approved_at: string | null;
  instruction_summary: string | null;
  instruction_steps: string[] | null;
  component_swap_slots: AssemblyComponentSwapSlot[] | null;
  material_plan: MaterialPlan | null;
}

export type MaterialPlanMode = "scratch" | "rebuild_from_existing";

export interface MaterialPlan {
  plan_mode: MaterialPlanMode;
  target_sku: string;
  source_kind: "hub_bom_sku" | "serial_fci";
  source_ref: string | null;
  declared_by: string;
  declared_at: string;
}

export interface MaterialPlanDiffLine {
  variable_name: string;
  source_component_sku: string | null;
  source_quantity: number;
  target_component_sku: string | null;
  target_quantity: number;
}

export interface MaterialPlanDiffResponse {
  target_sku: string;
  source_sku: string | null;
  plan_mode: MaterialPlanMode;
  lines: MaterialPlanDiffLine[];
  swap_slots: AssemblyComponentSwapSlot[];
  target_bom_resolved: boolean;
  note: string | null;
}

export interface AssemblyUnitRow {
  id: number;
  build_order_id: number;
  assembly_kind: string;
  pcba_serial: string;
  source_tla_sku: string | null;
  output_tla_sku: string;
  certified_at: string;
  certified_by: string;
  notes: string | null;
}

export interface HubBomLine {
  variable_name: string;
  component_sku: string | null;
  quantity: number;
}

export interface HubBomResponse {
  sku: string;
  templateId: number | null;
  revision: number | null;
  lines: HubBomLine[];
  source:
    | "hub_output_sku"
    | "hub_source_tla"
    | "megaventory_product"
    | "megaventory_source_tla"
    | "mg_shp_template"
    | "none";
  reference_sku: string | null;
  note: string | null;
}

export const ASSEMBLY_WORK_ORDERS_QUERY_KEY = ["manufacturing", "assembly", "work-orders"] as const;

export function fetchAssemblyKinds(): Promise<{ kinds: AssemblyKind[] }> {
  return apiFetch<{ kinds: AssemblyKind[] }>("/api/manufacturing/assembly/kinds");
}

export function fetchAssemblyWorkOrders(params?: {
  status?: string;
  search?: string;
}): Promise<
  WorkOrderListResponse & {
    workOrders: (WorkOrderListResponse["workOrders"][number] & AssemblyWorkOrderListRow)[];
  }
> {
  return apiFetch("/api/manufacturing/assembly/work-orders", {
    query: {
      status: params?.status ?? "active",
      search: params?.search ?? "",
    },
  });
}

export function fetchAssemblyWorkOrderDetail(id: number): Promise<AssemblyWorkOrderDetail> {
  return apiFetch<AssemblyWorkOrderDetail>(`/api/manufacturing/assembly/work-orders/${String(id)}`);
}

export function fetchAssemblyUnits(
  buildOrderId: number,
): Promise<{ work_order: AssemblyWorkOrderDetail; units: AssemblyUnitRow[] }> {
  return apiFetch(`/api/manufacturing/assembly/work-orders/${String(buildOrderId)}/units`);
}

export function createAssemblyWorkOrder(body: {
  assemblyKind: string;
  quantity: number;
  manufacturerReferenceNumber: string;
  comments?: string;
  materialPlanMode?: MaterialPlanMode;
  sourceSku?: string | null;
}): Promise<{ work_order: WorkOrderListResponse["workOrders"][number] & AssemblyWorkOrderListRow }> {
  return apiFetch("/api/manufacturing/assembly/work-orders", { method: "POST", body });
}

export function fetchAssemblyMaterialPlanDiff(params: {
  targetSku: string;
  sourceSku?: string | null;
  assemblyKind?: string | null;
  materialPlanMode?: MaterialPlanMode;
}): Promise<MaterialPlanDiffResponse> {
  return apiFetch<MaterialPlanDiffResponse>("/api/manufacturing/assembly/material-plan/diff", {
    query: {
      targetSku: params.targetSku,
      sourceSku: params.sourceSku ?? undefined,
      assemblyKind: params.assemblyKind ?? undefined,
      materialPlanMode: params.materialPlanMode ?? undefined,
    },
  });
}

export function recordAssemblyUnit(
  buildOrderId: number,
  body: { pcbaSerial: string; notes?: string },
): Promise<{ unit: AssemblyUnitRow; work_order: AssemblyWorkOrderDetail | null }> {
  return apiFetch(`/api/manufacturing/assembly/work-orders/${String(buildOrderId)}/assemble`, {
    method: "POST",
    body,
  });
}

export function fetchAssemblyHubBom(sku: string, assemblyKind?: string | null): Promise<HubBomResponse> {
  return apiFetch<HubBomResponse>("/api/manufacturing/assembly/hub-bom", {
    query: {
      sku,
      assemblyKind: assemblyKind ?? undefined,
    },
  });
}

export function printAssemblyLabel(body: {
  sku: string;
  printer: string;
  copies?: number;
}): Promise<{ printed: true; sku: string; copies: number }> {
  return apiFetch<{ printed: true; sku: string; copies: number }>(
    "/api/manufacturing/assembly/label/print",
    { method: "POST", body },
  );
}

export function isSerializedAssemblyRow(
  row: AssemblyWorkOrderListRow,
): row is AssemblyWorkOrderListRow & { assembly_type: "serialized"; certified_count: number } {
  return row.assembly_type === "serialized";
}
